import React, { useEffect, useState } from "react";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import withAuth from "../withAuth";
import { baseUrl } from "../../Network";
import { UploadImage, handleAuthError } from "../../utils";
import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TITLE_MAX_WORDS = 10;
const DESC_MAX_WORDS = 45;
const BUTTON_MAX_WORDS = 5;

const countWords = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const emptyForm = {
  title: "",
  description: "",
  imageUrl: "",
  buttonLabel: "",
  buttonLink: "",
  status: true,
  sortOrder: 0,
};

const ManageAdvert = () => {
  document.title = "Manage Advert | RYD Admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [adverts, setAdverts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const fetchAdverts = async () => {
    try {
      setLoading(true);
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await axios.get(`${baseUrl}/admin/advert/all`, { params });
      const data = response?.data?.data;
      setAdverts(Array.isArray(data) ? data : []);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to load adverts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdverts();
  }, [searchQuery]);

  const toggleModal = () => setModal((v) => !v);

  const openCreate = () => {
    setIsEdit(false);
    setSelected(null);
    setImageFile(null);
    setImagePreview("");
    validation.resetForm({ values: emptyForm });
    setModal(true);
  };

  const openEdit = (advert) => {
    setIsEdit(true);
    setSelected(advert);
    setImageFile(null);
    setImagePreview(advert.imageUrl || "");
    validation.resetForm({
      values: {
        title: advert.title || "",
        description: advert.description || "",
        imageUrl: advert.imageUrl || "",
        buttonLabel: advert.buttonLabel || "",
        buttonLink: advert.buttonLink || "",
        status: !!advert.status,
        sortOrder: Number(advert.sortOrder) || 0,
      },
    });
    setModal(true);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: emptyForm,
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .min(3, "Title is too short")
        .max(80, "Max 80 characters")
        .test(
          "max-words",
          `Title must be at most ${TITLE_MAX_WORDS} words`,
          (value) => !value || countWords(value) <= TITLE_MAX_WORDS
        )
        .required("Title is required"),
      description: Yup.string()
        .trim()
        .min(10, "Description is too short")
        .max(280, "Max 280 characters")
        .test(
          "max-words",
          `Description must be at most ${DESC_MAX_WORDS} words`,
          (value) => !value || countWords(value) <= DESC_MAX_WORDS
        )
        .required("Description is required"),
      buttonLabel: Yup.string()
        .trim()
        .min(2, "Button name is too short")
        .max(28, "Max 28 characters")
        .test(
          "max-words",
          `Button name must be at most ${BUTTON_MAX_WORDS} words`,
          (value) => !value || countWords(value) <= BUTTON_MAX_WORDS
        )
        .required("Button name is required"),
      buttonLink: Yup.string()
        .trim()
        .required("Button link is required")
        .test("is-http-url", "Enter a valid URL", (value) => {
          if (!value) return false;
          try {
            const parsed = new URL(value);
            return parsed.protocol === "http:" || parsed.protocol === "https:";
          } catch {
            return false;
          }
        }),
      status: Yup.boolean(),
      sortOrder: Yup.number().min(0),
    }),
    onSubmit: async (values) => {
      try {
        if (!isEdit && !imageFile && !values.imageUrl) {
          toast.error("Please upload an image");
          return;
        }
        setSaving(true);
        let imageUrl = values.imageUrl;
        if (imageFile) {
          imageUrl = await UploadImage(imageFile, "ryd-adverts");
          if (!imageUrl) {
            toast.error("Image upload failed");
            setSaving(false);
            return;
          }
        }

        const payload = {
          title: values.title.trim(),
          description: values.description.trim(),
          imageUrl,
          buttonLabel: values.buttonLabel.trim(),
          buttonLink: values.buttonLink.trim(),
          status: !!values.status,
          sortOrder: Number(values.sortOrder) || 0,
        };

        if (isEdit && selected?.id) {
          await axios.put(`${baseUrl}/admin/advert/edit/${selected.id}`, payload);
          toast.success("Advert updated");
        } else {
          await axios.post(`${baseUrl}/admin/advert/create`, payload);
          toast.success("Advert created");
        }
        setModal(false);
        fetchAdverts();
      } catch (error) {
        handleAuthError(error);
        toast.error(error?.response?.data?.message || "Save failed");
      } finally {
        setSaving(false);
      }
    },
  });

  const toggleStatus = async (advert) => {
    if (statusLoadingId) return;
    try {
      setStatusLoadingId(advert.id);
      await axios.put(`${baseUrl}/admin/advert/status/${advert.id}`, {
        status: !advert.status,
      });
      setAdverts((prev) =>
        prev.map((a) => (a.id === advert.id ? { ...a, status: !a.status } : a))
      );
      toast.success(advert.status ? "Advert hidden" : "Advert activated");
    } catch (error) {
      handleAuthError(error);
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      setStatusLoadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!selected?.id) return;
    try {
      await axios.delete(`${baseUrl}/admin/advert/${selected.id}`);
      toast.success("Advert deleted");
      setDeleteModal(false);
      setSelected(null);
      fetchAdverts();
    } catch (error) {
      handleAuthError(error);
      toast.error(error?.response?.data?.message || "Delete failed");
    }
  };

  const onImageChange = (e) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    validation.setFieldValue("imageUrl", "pending-upload");
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Advert" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center mb-3">
                <Col md={6}>
                  <h5 className="card-title mb-0">
                    Hero Adverts{" "}
                    <span className="text-muted fw-normal ms-2">({adverts.length})</span>
                  </h5>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: 13 }}>
                    Active adverts appear in the parent dashboard hero slider.
                  </p>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                    <Input
                      type="text"
                      placeholder="Search title..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ maxWidth: 220 }}
                    />
                    <Button color="primary" onClick={openCreate}>
                      Create Advert
                    </Button>
                  </div>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Button</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adverts.length > 0 ? (
                        adverts.map((advert, index) => (
                          <tr key={advert.id}>
                            <td>{index + 1}</td>
                            <td>
                              {advert.imageUrl ? (
                                <img
                                  src={advert.imageUrl}
                                  alt=""
                                  style={{
                                    width: 88,
                                    height: 48,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                  }}
                                />
                              ) : (
                                <span className="text-muted">No image</span>
                              )}
                            </td>
                            <td style={{ maxWidth: 220 }}>
                              <div className="fw-semibold">{advert.title}</div>
                              <div
                                className="text-muted"
                                style={{
                                  fontSize: 12,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {advert.description}
                              </div>
                            </td>
                            <td>
                              <div>{advert.buttonLabel}</div>
                              <a
                                href={advert.buttonLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 12 }}
                              >
                                Link
                              </a>
                            </td>
                            <td>{advert.sortOrder}</td>
                            <td>
                              {advert.status ? (
                                <Badge color="success">Active</Badge>
                              ) : (
                                <Badge color="secondary">Inactive</Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  color="primary"
                                  outline
                                  onClick={() => openEdit(advert)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  color={advert.status ? "warning" : "success"}
                                  outline
                                  disabled={statusLoadingId === advert.id}
                                  onClick={() => toggleStatus(advert)}
                                >
                                  {statusLoadingId === advert.id ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      {advert.status ? "Hiding..." : "Showing..."}
                                    </>
                                  ) : advert.status ? (
                                    "Hide"
                                  ) : (
                                    "Show"
                                  )}
                                </Button>
                                {advert.slug !== "ai-tutor-default" && (
                                  <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    onClick={() => {
                                      setSelected(advert);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <h5 className="mb-1">No adverts yet</h5>
                            <p className="text-muted mb-3">
                              Create one or restart the API to seed the default AI advert.
                            </p>
                            <Button color="primary" onClick={openCreate}>
                              Create Advert
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Advert" : "Create Advert"}
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label>
                Title{" "}
                <span className="text-muted">
                  (max {TITLE_MAX_WORDS} words / 80 chars · {countWords(validation.values.title)} words)
                </span>
              </Label>
              <Input
                name="title"
                value={validation.values.title}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.title && !!validation.errors.title}
                placeholder="Meet your child's AI Tutor"
              />
              {validation.touched.title && validation.errors.title && (
                <FormFeedback type="invalid">{validation.errors.title}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label>
                Description{" "}
                <span className="text-muted">
                  (max {DESC_MAX_WORDS} words / 280 chars · {countWords(validation.values.description)}{" "}
                  words)
                </span>
              </Label>
              <Input
                type="textarea"
                rows={3}
                name="description"
                value={validation.values.description}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.description && !!validation.errors.description}
              />
              {validation.touched.description && validation.errors.description && (
                <FormFeedback type="invalid">{validation.errors.description}</FormFeedback>
              )}
            </div>

            <div className="mb-3">
              <Label>Image (Cloudinary)</Label>
              <Input type="file" accept="image/*" onChange={onImageChange} />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-2"
                  style={{
                    width: "100%",
                    maxHeight: 180,
                    objectFit: "contain",
                    borderRadius: 10,
                    background: "#f4f4f4",
                  }}
                />
              )}
              <div className="text-muted mt-1" style={{ fontSize: 12, lineHeight: 1.45 }}>
                {!isEdit && !imageFile ? "Required for new adverts. " : ""}
                Recommended size: <strong>1600 × 533 px</strong> (≈ 3:1 wide landscape).
                Minimum: <strong>1200 × 400 px</strong>. Keep the left side relatively clear —
                title, description, and button overlay on the left.
              </div>
            </div>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>
                    Button name{" "}
                    <span className="text-muted">
                      (max {BUTTON_MAX_WORDS} words · {countWords(validation.values.buttonLabel)}{" "}
                      words)
                    </span>
                  </Label>
                  <Input
                    name="buttonLabel"
                    value={validation.values.buttonLabel}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.buttonLabel && !!validation.errors.buttonLabel
                    }
                    placeholder="Explore our AI"
                  />
                  {validation.touched.buttonLabel && validation.errors.buttonLabel && (
                    <FormFeedback type="invalid">{validation.errors.buttonLabel}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Button link</Label>
                  <Input
                    name="buttonLink"
                    value={validation.values.buttonLink}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.buttonLink && !!validation.errors.buttonLink}
                    placeholder="https://ai.rydlearning.com"
                  />
                  {validation.touched.buttonLink && validation.errors.buttonLink && (
                    <FormFeedback type="invalid">{validation.errors.buttonLink}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Sort order</Label>
                  <Input
                    type="number"
                    min={0}
                    name="sortOrder"
                    value={validation.values.sortOrder}
                    onChange={validation.handleChange}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3 form-check mt-4">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="advertStatus"
                    checked={!!validation.values.status}
                    onChange={(e) => validation.setFieldValue("status", e.target.checked)}
                  />
                  <Label className="form-check-label" htmlFor="advertStatus">
                    Active (visible in parent slider)
                  </Label>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggleModal} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? "Saving..." : isEdit ? "Update Advert" : "Create Advert"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withAuth(ManageAdvert);
